import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';
import { MemberIcon } from '@trello/nachos/icons/member';
import { token } from '@trello/theme';
import { learnMoreUrl } from '@trello/workspaces/user-limit';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';
import {
  useOpenPlanSelectionCallback,
  WithEnterpriseManagedOverride,
} from 'app/src/components/UpgradePrompts';

import styles from './TooltipContent.less';

export interface TooltipContentProps {
  workspaceId: string;
  onCtaClick?: () => void;
}

export const TooltipContent: FunctionComponent<TooltipContentProps> = ({
  workspaceId,
  onCtaClick,
}) => {
  const { isEligible } = useFreeTrialEligibilityRules(workspaceId, {
    skip: !workspaceId,
  });
  const openPlanSelection = useOpenPlanSelectionCallback(workspaceId, {
    redirect: false,
  });

  const onClickCta = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: isEligible ? 'startFreeTrialLink' : 'upgradeLink',
      source: 'workspaceUserLimitUpsellTooltip',
    });
    openPlanSelection();
    onCtaClick?.();
  }, [openPlanSelection, onCtaClick, isEligible]);

  const onClickLearnMore = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'learnMoreLink',
      source: 'workspaceUserLimitUpsellTooltip',
    });
    onCtaClick?.();
  }, [onCtaClick]);

  const tooltipAction = useMemo(() => {
    if (isEligible) {
      return (
        <a href="#" onClick={onClickCta}>
          <WithEnterpriseManagedOverride>
            <FormattedMessage
              id="templates.org_members.start-free-trial"
              defaultMessage="Start free trial"
              description="Start free trial link"
            />
          </WithEnterpriseManagedOverride>
        </a>
      );
    } else if (dontUpsell()) {
      return (
        <a href={learnMoreUrl} target="_blank" onClick={onClickLearnMore}>
          <FormattedMessage
            id="templates.org_members.learn-more"
            defaultMessage="Learn more"
            description="Learn more link"
          />
        </a>
      );
    } else {
      return (
        <a href="#" onClick={onClickCta}>
          <WithEnterpriseManagedOverride>
            <FormattedMessage
              id="templates.org_members.upgrade"
              defaultMessage="Upgrade"
              description="Upgrade link"
            />
          </WithEnterpriseManagedOverride>
        </a>
      );
    }
  }, [isEligible, onClickCta, onClickLearnMore]);

  return (
    <div className={styles.tooltipContent}>
      <h3>
        <FormattedMessage
          id="templates.org_members.this-free-workspace-has-reached-the-10-collaborator-limit"
          defaultMessage="This free Workspace has reached the 10 collaborator limit."
          description="This free Workspace has reached the 10 collaborator limit."
        />
      </h3>
      <p>
        {dontUpsell() && !isEligible ? (
          <FormattedMessage
            id="templates.org_members.to-add-new-collaborators-remove-members-guests-or-pending-invitations"
            defaultMessage="To add new collaborators, remove members, guests, or pending invitations."
            description="To add new collaborators, remove members, guests, or pending invitations."
          />
        ) : (
          <FormattedMessage
            id="templates.org_members.to-invite-more-collaborators-upgrade-this-workspace"
            defaultMessage="To invite more collaborators, upgrade this Workspace."
            description="To invite more collaborators, upgrade this Workspace."
          />
        )}
      </p>
      <div className={styles.ctaContainer}>{tooltipAction}</div>
      <div className={styles.iconContainer}>
        <MemberIcon
          size="large"
          color={token('color.icon.inverse', '#FFFFFF')}
        />
      </div>
      <div className={styles.iconBackground} />
    </div>
  );
};
